import { compact, difference } from 'lodash';

import { BOTH_GRANTORS_SENTINEL } from '@/hooks/useDualGrantorOptions';
import {
  HypotheticalInsuranceTransferTaxKind,
  UpdateHypotheticalInsuranceInput,
} from '@/types/schema';

import { MapHypotheticalInsuranceToDataInput } from '../HypotheticalILITModal.utils';

export function getCreateTransferTax({
  formData,
  primaryClients,
}: MapHypotheticalInsuranceToDataInput): {
  transferTaxKind: HypotheticalInsuranceTransferTaxKind;
  giftingGrantorIDs: string[] | undefined;
} {
  const { transferTaxKind: transferTaxKindString } = formData;

  let transferTaxKind: HypotheticalInsuranceTransferTaxKind;
  let giftingGrantorIDs: string[] | undefined;

  if (
    transferTaxKindString ===
    HypotheticalInsuranceTransferTaxKind.AnnualExclusionGiftWithCrummeyPowers
  ) {
    transferTaxKind =
      HypotheticalInsuranceTransferTaxKind.AnnualExclusionGiftWithCrummeyPowers;
  } else if (transferTaxKindString === BOTH_GRANTORS_SENTINEL) {
    transferTaxKind = HypotheticalInsuranceTransferTaxKind.GrantorTaxableGift;
    giftingGrantorIDs = primaryClients?.map((client) => client.id);
  } else if (
    transferTaxKindString === HypotheticalInsuranceTransferTaxKind.NonTaxable
  ) {
    transferTaxKind = HypotheticalInsuranceTransferTaxKind.NonTaxable;
    giftingGrantorIDs = undefined;
  } else {
    transferTaxKind = HypotheticalInsuranceTransferTaxKind.GrantorTaxableGift;
    giftingGrantorIDs = [transferTaxKindString];
  }

  return {
    transferTaxKind,
    giftingGrantorIDs,
  };
}

type UpdateTransferTaxOutput = Partial<
  Pick<
    UpdateHypotheticalInsuranceInput,
    'addGiftingGrantorIDs' | 'removeGiftingGrantorIDs' | 'clearGiftingGrantors'
  >
> &
  Pick<UpdateHypotheticalInsuranceInput, 'transferTaxKind'>;
export function getUpdateTransferTax({
  formData,
  primaryClients,
  contextData: { initialHypotheticalInsurance },
}: MapHypotheticalInsuranceToDataInput): UpdateTransferTaxOutput {
  const { transferTaxKind: transferTaxKindString } = formData;
  const initialGiftingGrantorIDs = compact(
    initialHypotheticalInsurance?.giftingGrantors?.map((g) => g.id)
  );

  if (
    transferTaxKindString ===
    HypotheticalInsuranceTransferTaxKind.AnnualExclusionGiftWithCrummeyPowers
  ) {
    return {
      transferTaxKind:
        HypotheticalInsuranceTransferTaxKind.AnnualExclusionGiftWithCrummeyPowers,
      clearGiftingGrantors: true,
    };
  } else if (transferTaxKindString === BOTH_GRANTORS_SENTINEL) {
    const currentGiftingGrantorIDs = compact(primaryClients?.map((c) => c.id));
    const addGiftingGrantorIDs: string[] = difference(
      currentGiftingGrantorIDs,
      initialGiftingGrantorIDs
    );
    const removeGiftingGrantorIDs: string[] = difference(
      initialGiftingGrantorIDs,
      currentGiftingGrantorIDs
    );
    return {
      transferTaxKind: HypotheticalInsuranceTransferTaxKind.GrantorTaxableGift,
      addGiftingGrantorIDs,
      removeGiftingGrantorIDs,
    };
  } else if (
    transferTaxKindString === HypotheticalInsuranceTransferTaxKind.NonTaxable
  ) {
    return {
      transferTaxKind: HypotheticalInsuranceTransferTaxKind.NonTaxable,
      clearGiftingGrantors: true,
    };
  } else {
    const currentGiftingGrantorIDs = [transferTaxKindString];
    const addGiftingGrantorIDs: string[] = difference(
      currentGiftingGrantorIDs,
      initialGiftingGrantorIDs
    );
    const removeGiftingGrantorIDs: string[] = difference(
      initialGiftingGrantorIDs,
      currentGiftingGrantorIDs
    );
    return {
      transferTaxKind: HypotheticalInsuranceTransferTaxKind.GrantorTaxableGift,
      addGiftingGrantorIDs,
      removeGiftingGrantorIDs,
    };
  }
}
